import { useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../contexts/AuthContext';
import { 
  UserRole, 
  Permission, 
  PERMISSIONS, 
  hasPermission, 
  getPermissionsForRole, 
  canPerformAction 
} from '../types/permissions';

// Helper function to extract role from JWT token
const extractRoleFromToken = (): UserRole | null => {
  try {
    const idToken = localStorage.getItem('id_token');
    if (!idToken) return null;

    // Use proper JWT decoding library instead of atob()
    const payload = jwtDecode(idToken) as any;
    
    // Check for role in custom attributes
    const role = payload['custom:userRole'] || payload.userRole;
    
    if (role) {
      // Convert backend role to frontend role if needed
      const roleMapping: Record<string, UserRole> = {
        'TenantAdmin': 'Administrator',
        'TenantPowerUser': 'PowerUser',
        'TenantUser': 'ReadOnly',
        'Administrator': 'Administrator',
        'PowerUser': 'PowerUser',
        'ReadOnly': 'ReadOnly',
      };
      
      return roleMapping[role] || null;
    }
    
    return null;
  } catch (error) {
    // Only log errors in development mode
    if (process.env.NODE_ENV === 'development') {
      console.error('Error extracting role from token:', error);
    }
    return null;
  }
};

export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();
  
  const userRole = useMemo((): UserRole | null => {
    if (!isAuthenticated || !user) return null;
    
    // Try to get role from token first
    const tokenRole = extractRoleFromToken();
    if (tokenRole) return tokenRole;
    
    // Fallback: check if role is stored in user object
    // This might be needed if the role is stored differently
    const userRoleFromUser = (user as any).role;
    if (userRoleFromUser) return userRoleFromUser;
    
    // Default to ReadOnly if no role is found
    if (process.env.NODE_ENV === 'development') {
      console.warn('No role found for user, defaulting to ReadOnly');
    }
    return 'ReadOnly';
  }, [user, isAuthenticated]);

  const permissions = useMemo((): Permission[] => {
    if (!userRole) return [];
    return getPermissionsForRole(userRole);
  }, [userRole]);

  const checkPermission = (requiredRole: UserRole): boolean => {
    if (!userRole) return false;
    return hasPermission(userRole, requiredRole);
  };

  const checkActionPermission = (permissionId: keyof typeof PERMISSIONS): boolean => {
    if (!userRole) return false;
    return canPerformAction(userRole, permissionId);
  };

  const isReadOnly = useMemo(() => userRole === 'ReadOnly', [userRole]);
  const isPowerUser = useMemo(() => userRole === 'PowerUser', [userRole]);
  const isAdministrator = useMemo(() => userRole === 'Administrator', [userRole]);

  return {
    userRole,
    permissions,
    hasPermission: checkPermission,
    canPerformAction: checkActionPermission,
    isReadOnly,
    isPowerUser,
    isAdministrator,
    isAuthenticated,
  };
};
